import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Wrench, Package, Calendar, User, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpareDetail = () => {
  const { machineId, spareId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showAddStock, setShowAddStock] = useState(false);
  const [stockQty, setStockQty] = useState("");
  const [stockNotes, setStockNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchDetail(); }, [machineId, spareId]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/machines/${machineId}/spares/${spareId}/detail`);
      setDetail(res.data);
    } catch (err) {
      toast.error("Failed to load spare details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddStock = async () => {
    if (!stockQty || parseInt(stockQty) <= 0) { toast.error("Enter a valid quantity"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/machines/${machineId}/spares/${spareId}/stock`, {
        quantity: parseInt(stockQty),
        notes: stockNotes,
        date_added: new Date().toISOString(),
      });
      toast.success("Stock added successfully");
      setShowAddStock(false);
      setStockQty("");
      setStockNotes("");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-gray-400 text-lg">Spare not found</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="mt-4 text-gray-300">Go Back</Button>
      </div>
    );
  }

  const { spare, machine_name, stocks, usage_logs, total_added, total_used, available } = detail;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/workorder/machines`)} className="text-gray-400 hover:text-white" data-testid="back-to-machines">
              <ArrowLeft className="h-5 w-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Wrench className="h-6 w-6 text-amber-400" />
                {spare.spare_name || spare.name}
              </h1>
              <p className="text-gray-400 text-sm">{machine_name} ({machineId}) &middot; {spare.spare_id}</p>
            </div>
          </div>
          <Button onClick={() => setShowAddStock(true)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="add-stock-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Stock
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-gray-800/50 border border-green-500/20">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg"><Package className="h-7 w-7 text-green-400" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{total_added}</p>
                <p className="text-sm text-gray-400">Total Added</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-red-500/20">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 bg-red-500/20 rounded-lg"><TrendingDown className="h-7 w-7 text-red-400" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{total_used}</p>
                <p className="text-sm text-gray-400">Total Used</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border border-amber-500/20">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 bg-amber-500/20 rounded-lg"><Wrench className="h-7 w-7 text-amber-400" /></div>
              <div>
                <p className={`text-2xl font-bold ${available <= 0 ? 'text-red-400' : 'text-white'}`}>{available}</p>
                <p className="text-sm text-gray-400">Available</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stock Entries */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardHeader className="border-b border-gray-700">
            <CardTitle className="text-lg text-amber-400">Stock Entries</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {stocks.length === 0 ? (
              <p className="text-gray-500 text-center py-6">No stock entries yet. Click "Add Stock" to add the first batch.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-amber-400">Stock ID</TableHead>
                    <TableHead className="text-amber-400 text-center">Quantity Added</TableHead>
                    <TableHead className="text-amber-400">Date Added</TableHead>
                    <TableHead className="text-amber-400">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stocks.map((stk) => (
                    <TableRow key={stk.id} className="border-gray-700">
                      <TableCell>
                        <span className="font-mono text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                          {stk.stock_id}
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-white font-bold">{stk.quantity_added}</TableCell>
                      <TableCell className="text-gray-300 text-sm">
                        {stk.date_added ? new Date(stk.date_added).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">{stk.notes || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Usage History */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader className="border-b border-gray-700">
            <CardTitle className="text-lg text-red-400">Usage History (Stock Out)</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {usage_logs.length === 0 ? (
              <p className="text-gray-500 text-center py-6">No usage recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-red-400">Date</TableHead>
                    <TableHead className="text-red-400 text-center">Qty Out</TableHead>
                    <TableHead className="text-red-400">Person</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage_logs.map((log) => (
                    <TableRow key={log.id} className="border-gray-700">
                      <TableCell className="text-gray-300 text-sm flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-gray-500" />
                        {log.date_used ? new Date(log.date_used).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                      </TableCell>
                      <TableCell className="text-center text-red-400 font-bold">-{log.quantity_out}</TableCell>
                      <TableCell className="text-gray-300 text-sm flex items-center gap-2">
                        <User className="h-3 w-3 text-gray-500" />
                        {log.person_name}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Stock Dialog */}
      <Dialog open={showAddStock} onOpenChange={setShowAddStock}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <Plus className="h-5 w-5" /> Add New Stock Batch
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Quantity *</Label>
              <Input type="number" min="1" value={stockQty} onChange={(e) => setStockQty(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" placeholder="e.g., 40" data-testid="stock-qty-input" />
            </div>
            <div>
              <Label className="text-gray-300">Notes (optional)</Label>
              <Input value={stockNotes} onChange={(e) => setStockNotes(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" placeholder="e.g., Purchased from XYZ" data-testid="stock-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStock(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleAddStock} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="stock-save-btn">
              {saving ? "Saving..." : "Add Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpareDetail;
